import { isValidUuid } from '@/util/is-valid-uuid'
import { renderTableLink, renderTableCards } from '@/tables/components/TableLinkRenderer'
import { useContext, useEffect, useRef, useState } from 'react'
import { kRouteSegments } from '@/routing/paths'
import ErrorComponent from '@/components/error/error-component'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useParams, useNavigate } from 'react-router-dom'
import { RootState } from '@/store/store'
import CircularProgress from '@mui/material/CircularProgress'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { setActiveConversationId } from '@/chat-common/store/chat-ux.slice'
import { AuthContext } from '@/context/auth-context'
import AgentV2ChatWindow from '@/agent-v2/components/AgentV2ChatWindow'
import {
  selectAgentV2ConversationDetail,
  selectAgentV2ConversationKnownDeleted,
  selectAgentV2ConversationFromId,
  selectAgentV2ConversationLoadError,
  selectAgentV2ConversationsByMatter,
  selectAgentV2InitialLoadComplete,
  selectAgentV2ListError,
  selectMostRecentAgentV2NewChat,
} from '@/agent-v2/conversations/store/selectors'
import { Button } from '@/components/button/Button'
import { AgentV2ConversationsActions, AgentV2DetailLoadError } from '@/agent-v2/conversations/store/slice'
import { ConversationNavigationActions } from '@/chat-common/conversation-navigation/store/slice'
import { selectLastViewedConversationId } from '@/chat-common/conversation-navigation/store/selectors'
import { buildChatFeaturePath, buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'

import { AgentV2ConversationSurface } from '@/agent-v2/store/schemas'
import AgentV2ChatLayout from '@/routes/dashboard/AgentV2ChatLayout'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useLoadMatterArtifactVersion } from '@/matter-artifacts/hooks/useLoadMatterArtifactVersion'
import ReadOnlyMatterArtifact from '@/matter-artifacts/components/ReadOnlyMatterArtifact'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { ensureOutlookPrivacyMode, isOutlookPrivacyMode } from '@/analytics/privacy/outlook-continuation-privacy'
import { selectMatterById } from '@/matter/store/selectors'
import { MatterType } from '@/matter/store/schemas'

function AddInDraftViewer({ conversationId, matterArtifactVersionId }: { conversationId: string; matterArtifactVersionId: string }) {
  const { isLoaded, hasError, retry } = useLoadMatterArtifactVersion(matterArtifactVersionId)

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden border-t border-border-default bg-surface-emphasis">
      {hasError ? (
        <ContentViewerErrorState message="Could not load this draft." onRetry={retry} />
      ) : isLoaded ? (
        <ReadOnlyMatterArtifact conversationId={conversationId} matterArtifactVersionId={matterArtifactVersionId} openSourcesInChatPanel={false} />
      ) : (
        <ContentViewerLoadingSpinner size="large" />
      )}
    </div>
  )
}

/** Resolves and guards the conversation for the chat route. */
export default function AgentV2ChatRoute({
  embeddedConversationId,
  onOpenExternalArtifact,
}: {
  embeddedConversationId?: string
  onOpenExternalArtifact?: (versionId: string) => void
} = {}) {
  const { chatId: routeChatId, matterId, tableId } = useParams()
  const openTableId = tableId
  const chatId = embeddedConversationId ?? routeChatId
  const currentUserId = useContext(AuthContext)?.user?.uid
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()
  const { draftArtifactVersionId } = useDraftWorkspace()
  // Scoped by matter id: create-in-flight for matter A must not block matter B (PR #165 review)
  const creatingConversationRef = useRef<string | null>(null)
  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const agentV2InitialLoadComplete = useAppSelector((state: RootState) => selectAgentV2InitialLoadComplete(state, { matterId: matterId ?? null }))
  const agentV2ListError = useAppSelector((state: RootState) => selectAgentV2ListError(state, { matterId: matterId ?? null }))
  const agentV2Conversations = useAppSelector((state: RootState) => selectAgentV2ConversationsByMatter(state, { matterId: matterId ?? null, currentUserId }))
  const ownAgentV2NewChat = useAppSelector((state: RootState) => selectMostRecentAgentV2NewChat(state, { matterId: matterId ?? null, currentUserId }))
  const lastViewedAgentV2ConversationId = useAppSelector((state: RootState) => selectLastViewedConversationId(state, { matterId: matterId ?? null }))
  const agentV2Conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId: chatId }))
  const agentV2ConversationKnownDeleted = useAppSelector((state: RootState) => selectAgentV2ConversationKnownDeleted(state, { conversationId: chatId }))
  const agentV2ConversationDetail = useAppSelector((state: RootState) => selectAgentV2ConversationDetail(state, { conversationId: chatId }))
  const agentV2ConversationLoadError = useAppSelector((state: RootState) => selectAgentV2ConversationLoadError(state, { conversationId: chatId }))
  const [show404, setShow404] = useState(false)
  const [autoCreateErrorMatterId, setAutoCreateErrorMatterId] = useState<string | null>(null)

  const initialLoadComplete = mattersInitialLoadComplete && agentV2InitialLoadComplete
  const agentV2ConversationMatterId = agentV2ConversationDetail?.conversation.matter_id ?? agentV2Conversation?.matter_id
  const routeMatterMismatch = Boolean(matterId && agentV2ConversationMatterId && agentV2ConversationMatterId !== matterId)
  // Outlook keeps its origin while continuing in the browser; Word remains host-only.
  const addInConversationSurface =
    agentV2Conversation?.origin_surface !== undefined &&
    agentV2Conversation.origin_surface !== AgentV2ConversationSurface.WEB &&
    agentV2Conversation.origin_surface !== AgentV2ConversationSurface.OUTLOOK
      ? agentV2Conversation.origin_surface
      : null
  const isAddInOnlyConversation = addInConversationSurface !== null || agentV2ConversationLoadError === AgentV2DetailLoadError.ADD_IN_ONLY
  const addInHostName = 'Word'
  const needsOutlookPrivacy = agentV2Conversation?.origin_surface === AgentV2ConversationSurface.OUTLOOK && !isOutlookPrivacyMode()
  useEffect(() => {
    if (needsOutlookPrivacy) ensureOutlookPrivacyMode(AgentV2ConversationSurface.OUTLOOK)
  }, [needsOutlookPrivacy])

  useEffect(() => {
    if (!matterId) return
    dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: false }))
  }, [dispatch, matterId])

  useEffect(() => {
    if (chatId || !matterId || !initialLoadComplete || creatingConversationRef.current === matterId) return

    const lastViewedConversation = lastViewedAgentV2ConversationId
      ? agentV2Conversations.find((conversation) => conversation.id === lastViewedAgentV2ConversationId)
      : null
    const targetConversation = lastViewedConversation ?? ownAgentV2NewChat ?? agentV2Conversations[0] ?? null

    if (targetConversation) {
      navigate(buildConversationPath(matterId, targetConversation.id, kRouteSegments.agentV2), { replace: true })
      return
    }

    // A failed list load must never look like an empty matter — creating here would duplicate
    // conversations that exist server-side (ENG-7069)
    if (agentV2ListError) return
    if (matter?.type === MatterType.OUTLOOK_WORKSPACE) return

    // After a failed create, wait for the user to retry instead of looping (ENG-7070)
    if (autoCreateErrorMatterId === matterId) return

    creatingConversationRef.current = matterId
    void createNewAgentV2Chat(
      true,
      matterId,
      () => {
        if (creatingConversationRef.current === matterId) creatingConversationRef.current = null
      },
      () => {
        if (creatingConversationRef.current === matterId) creatingConversationRef.current = null
        setAutoCreateErrorMatterId(matterId)
      },
      { replace: true }
    )
  }, [
    agentV2Conversations,
    agentV2ListError,
    autoCreateErrorMatterId,
    chatId,
    createNewAgentV2Chat,
    initialLoadComplete,
    lastViewedAgentV2ConversationId,
    matterId,
    matter?.type,
    navigate,
    ownAgentV2NewChat,
  ])

  useEffect(() => {
    if (embeddedConversationId || !chatId || !matterId || !agentV2ConversationKnownDeleted) return
    navigate(buildChatFeaturePath(matterId, kRouteSegments.agentV2), { replace: true })
  }, [agentV2ConversationKnownDeleted, chatId, embeddedConversationId, matterId, navigate])

  useEffect(() => {
    if (!chatId || agentV2ConversationKnownDeleted || !isValidUuid(chatId) || routeMatterMismatch) return
    dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId: chatId, forceRefresh: false }))
  }, [agentV2ConversationKnownDeleted, chatId, dispatch, routeMatterMismatch])

  useEffect(() => {
    if (!chatId || !isValidUuid(chatId) || routeMatterMismatch) return
    dispatch(setActiveConversationId({ conversationId: chatId }))
    return () => {
      dispatch(setActiveConversationId({ conversationId: null }))
    }
  }, [chatId, dispatch, routeMatterMismatch])

  useEffect(() => {
    // Only conversations admitted on the browser become its last-viewed chat.
    if (!agentV2Conversation || !matterId || agentV2Conversation.matter_id !== matterId || isAddInOnlyConversation) return
    dispatch(
      ConversationNavigationActions.setLastViewedByMatter({
        conversation: agentV2Conversation,
        activeMatterId: matterId,
      })
    )
  }, [agentV2Conversation, dispatch, isAddInOnlyConversation, matterId])

  useEffect(() => {
    // Only an invalid id or a server-confirmed 404 is "not found" — retryable failures render inline with Retry (ENG-7070)
    if ((chatId && !isValidUuid(chatId)) || (chatId && agentV2ConversationLoadError === AgentV2DetailLoadError.NOT_FOUND)) {
      const id = setTimeout(() => setShow404(true), 800)
      return () => clearTimeout(id)
    }
    setShow404(false)
  }, [agentV2ConversationLoadError, chatId])

  // Keyed on chatId so switching conversations clears the previous chat's viewer session (ENG-7068)
  useEffect(() => {
    // The table sidebar owns the source when this chat is embedded. Mounting the chat
    // to open a footnote must not clear that source during StrictMode's effect replay.
    if (embeddedConversationId) return
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [chatId, dispatch, embeddedConversationId])

  if (!chatId || !isValidUuid(chatId)) {
    if (!chatId && initialLoadComplete && !agentV2ListError && matter?.type === MatterType.OUTLOOK_WORKSPACE && agentV2Conversations.length === 0) {
      return <InlineMessage>There are no saved Outlook chats yet. Start a chat in the Outlook add-in, then continue it here.</InlineMessage>
    }
    if (show404) {
      return <ErrorComponent code={'404'} title={'Chat not found'} message={`We could not find the chat with id: ${chatId}`} />
    }

    if (!chatId && matterId && agentV2ListError) {
      return (
        <div className="flex w-full h-full flex-col items-center justify-center gap-3">
          <InlineMessage variant="error">Unable to load chats for this matter.</InlineMessage>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            size="sm"
            onClick={() => dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true }))}
          >
            Retry
          </Button>
        </div>
      )
    }

    if (!chatId && matterId && autoCreateErrorMatterId === matterId) {
      return (
        <div className="flex w-full h-full flex-col items-center justify-center gap-3">
          <InlineMessage variant="error">Unable to create a new chat.</InlineMessage>
          <Button type="button" variant="outlined" color="primary" size="sm" onClick={() => setAutoCreateErrorMatterId(null)}>
            Retry
          </Button>
        </div>
      )
    }

    return (
      <div className="flex items-center justify-center w-full h-full">
        <CircularProgress size={24} thickness={5} />
      </div>
    )
  }

  if (routeMatterMismatch || (embeddedConversationId && agentV2ConversationKnownDeleted)) {
    return <ErrorComponent code={'404'} title={'Chat not found'} message={`We could not find the chat with id: ${chatId}`} />
  }

  if (show404) {
    return <ErrorComponent code={'404'} title={'Chat not found'} message={`We could not find the chat with id: ${chatId}`} />
  }

  if (isAddInOnlyConversation) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <h1 className="text-lg font-semibold">This conversation is available in {addInHostName}</h1>
          <p>Open it from the Paxton add-in to continue. {addInHostName} conversations do not appear in browser chat history.</p>
          <Button variant="outlined" onClick={() => matterId && navigate(buildChatFeaturePath(matterId, kRouteSegments.agentV2), { replace: true })}>
            Go to browser chats
          </Button>
        </div>
        {draftArtifactVersionId && <AddInDraftViewer conversationId={chatId} matterArtifactVersionId={draftArtifactVersionId} />}
      </div>
    )
  }

  if (!agentV2ConversationDetail && agentV2ConversationLoadError === AgentV2DetailLoadError.RETRYABLE) {
    return (
      <div className="flex w-full h-full flex-col items-center justify-center gap-3">
        <InlineMessage variant="error">Unable to load this chat.</InlineMessage>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="sm"
          onClick={() => dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId: chatId, forceRefresh: true }))}
        >
          Retry
        </Button>
      </div>
    )
  }

  // The conversation body only renders from loaded detail — never from a bare summary (ENG-7043)
  if (!agentV2ConversationDetail) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <CircularProgress size={24} thickness={5} />
      </div>
    )
  }

  if (needsOutlookPrivacy) return null

  if (embeddedConversationId) {
    return (
      <AgentV2ChatWindow
        renderLink={renderTableLink}
        renderResponseFooter={(message) => renderTableCards(agentV2ConversationDetail.conversation.matter_id, message.tableCardIds ?? [])}
        key={chatId}
        conversationId={chatId}
        openTableId={openTableId}
        onOpenExternalArtifact={onOpenExternalArtifact}
      />
    )
  }

  return (
    <AgentV2ChatLayout conversationId={chatId}>
      {agentV2ConversationDetail.conversation.origin_surface === AgentV2ConversationSurface.OUTLOOK && (
        <InlineMessage variant="info">
          Started in Outlook. Continue using the saved email and attachments in this chat. Paxton cannot see your currently open email.
        </InlineMessage>
      )}
      <AgentV2ChatWindow
        renderLink={renderTableLink}
        renderResponseFooter={(message) => renderTableCards(agentV2ConversationDetail.conversation.matter_id, message.tableCardIds ?? [])}
        key={chatId}
        conversationId={chatId}
        openTableId={openTableId}
      />
    </AgentV2ChatLayout>
  )
}
